import { useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { logger } from "@/utils/logger";
import { handleUnauthorizedRedirect } from "@/utils/authErrorHandler";
import { UnauthorizedError } from "@/utils/errors";

const AIEW_BASE_URL = import.meta.env.VITE_AIEW_URL || "https://ai-edge.io";

interface RecentBoost {
  expiresAt: string;
  purchasedAt: string;
  durationDays: number;
  refundEligible: boolean;
  refundRequested: boolean;
}

interface BoostStatus {
  eligible: boolean;
  activeBoost: { expiresAt: string; purchasedAt: string } | null;
  recentBoost?: RecentBoost | null;
  cooldownUntil?: string | null;
  boostChainCount?: number | null;
}

export interface UseBoostStatusResult {
  eligible: boolean;
  activeBoost: BoostStatus["activeBoost"];
  recentBoost: RecentBoost | null;
  cooldownUntil: string | null;
  boostChainCount: number | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

async function fetchBoostStatus(): Promise<BoostStatus> {
  const response = await fetch(
    `${AIEW_BASE_URL}/api/stripe/boost/status`,
    { credentials: "include" }
  );

  if (response.status === 401) {
    handleUnauthorizedRedirect();
    throw new UnauthorizedError({ recoverable: false });
  }

  if (!response.ok) {
    throw new Error(`Boost status fetch failed: ${response.status}`);
  }

  return response.json();
}

export function useBoostStatus(
  boostEnabled: boolean,
  flagStatus: "loading" | "loaded" | "error",
  options?: { refetchInterval?: number | false }
): UseBoostStatusResult {
  // Prevent duplicate error logs across renders (intentionally not reset on remount)
  const hasLoggedError = useRef(false);

  const { data, status, error, refetch } = useQuery({
    queryKey: ["boost-status"],
    queryFn: fetchBoostStatus,
    enabled: boostEnabled && flagStatus !== "loading",
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    retry: (failureCount, err) => {
      if (err instanceof UnauthorizedError) return false;
      return failureCount < 1;
    },
    refetchInterval: options?.refetchInterval ?? false,
  });

  if (error && !hasLoggedError.current) {
    logger.warn("Boost status fetch failed", error);
    hasLoggedError.current = true;
  }

  return {
    eligible: data?.eligible ?? false,
    activeBoost: data?.activeBoost ?? null,
    recentBoost: data?.recentBoost ?? null,
    cooldownUntil: data?.cooldownUntil ?? null,
    boostChainCount: data?.boostChainCount ?? null,
    isLoading: status === "pending" && boostEnabled && flagStatus !== "loading",
    isError: status === "error",
    error: error ?? null,
    refetch,
  };
}
